import { useMemo } from "react";
import { asNumber, formatCompactValue, formatValue, toneColor } from "../../lib/kernelFormat.js";
import { boundedLimit, linearPercent, pctDelta } from "../../lib/decisionScales.js";
import { sanitizeRenderColor } from "../../lib/renderSafety.js";
import { KernelEmpty } from "./shared.jsx";

export const kind = "metricRail";

function formatRatioPercent(ratio) {
    const percent = ratio * 100;
    return `${percent.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

function deltaTone(delta, direction) {
    if (delta == null || delta === 0 || !["up", "down"].includes(direction)) return "var(--muted-soft)";
    const favorable = direction === "down" ? delta < 0 : delta > 0;
    return favorable ? "var(--green)" : "var(--red, #c0392b)";
}

export function Component({ spec, data, fillHeight = false }) {
    const maxItems = boundedLimit(spec.maxItems, 8, 12);
    const rows = useMemo(
        () => (Array.isArray(data)
            ? data.filter((row) => row?.label != null && asNumber(row.value) != null).slice(0, maxItems)
            : []),
        [data, maxItems],
    );
    if (!rows.length) return <KernelEmpty message={spec.empty || "No metrics to show"} />;

    return (
        <div className={fillHeight ? "flex h-full min-h-0 flex-col" : ""}>
            {spec.title ? (
                <div className="mb-[6px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]">{spec.title}</div>
            ) : null}
            <div className="divide-y divide-[color:var(--line)]">
                {rows.map((row, index) => {
                    const format = row.format || spec.format || "number";
                    const current = asNumber(row.value);
                    const reference = asNumber(row.reference);
                    const delta = row.delta == null
                        ? (current != null && reference != null ? pctDelta(reference, current) : null)
                        : asNumber(row.delta);
                    const direction = ["up", "down", "neutral"].includes(row.favorable) ? row.favorable : "neutral";
                    const color = deltaTone(delta, direction);
                    const scaleMax = Math.max(
                        Math.abs(current ?? 0),
                        Math.abs(reference ?? 0),
                    ) * 1.1 || 1;
                    return (
                        <div key={row.id || `${row.label}-${index}`} className="py-[7px]">
                            <div className="flex items-baseline justify-between gap-[8px]">
                                <span className="text-[9.5px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">{row.label}</span>
                                {delta != null && Number.isFinite(delta) ? (
                                    <span className="shrink-0 font-mono text-[9.5px] tabular-nums" style={{ color }}>
                                        {delta > 0 ? "+" : ""}{formatRatioPercent(delta)}
                                    </span>
                                ) : null}
                            </div>
                            <div className="mt-[1px] flex items-baseline justify-between gap-[8px]">
                                <span
                                    className="font-mono text-[17px] leading-[1.1] tabular-nums"
                                    style={{ color: row.tone ? toneColor(row.tone) : "var(--ink)" }}
                                    title={formatValue(row.value, format)}
                                >
                                    {formatCompactValue(current, format)}
                                </span>
                                {row.referenceLabel ? (
                                    <span className="text-[9.5px] text-[color:var(--muted-soft)]">{row.referenceLabel}</span>
                                ) : null}
                            </div>
                            {current != null && reference != null && current >= 0 && reference >= 0 ? (
                                <div className="relative mt-[5px] h-[7px] bg-[color:var(--line-soft)]" aria-label={`Current ${formatValue(current, format)}; reference ${formatValue(reference, format)}`}>
                                    <div className="absolute inset-y-0 left-0 opacity-40" style={{ width: `${linearPercent(Math.max(0, current), scaleMax)}%`, background: sanitizeRenderColor(color) }} />
                                    <div className="absolute -top-[2px] h-[11px] w-[2px] bg-[color:var(--ink)]" style={{ left: `${linearPercent(Math.max(0, reference), scaleMax)}%` }} />
                                </div>
                            ) : null}
                            {row.note ? <div className="mt-[3px] text-[9.5px] leading-[1.35] text-[color:var(--muted-soft)]">{row.note}</div> : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
